import React, { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { css } from '@emotion/css';
import { useSelector } from 'react-redux';
import { UserState } from '@features/Auth/user.slice';
import { RootState } from '@redux/store';
import { Link, useLocation } from 'react-router-dom';
import HertzLogoType from '@components/HertzLogoType/HertzLogoType';
import { MAIN_LINKS, SUB_LINKS } from './constants/links';
import { useRegisterTestCarMutation } from '@services/devApi';
import { shouldShowDevtools } from '@utils/locationHelper';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const {t} = useTranslation()
  const theme = useTheme();
  const activeLocation = useLocation();
  const userState: UserState = useSelector((state: RootState) => state.user);
  const isActive = (path: string) => activeLocation.pathname === path;

  const [registrationNumber, setRegistrationNumber] = useState<string|null>(null)

  const [registerNewCar] = useRegisterTestCarMutation()

  const styles = {
    container: css`
      height: ${userState?.user?.signedIn ? '27rem' : '6rem'};
      padding: 1rem 1rem;
      ${theme.customValues.flexCenter}
      flex-direction: column;
      background-color: #f3f4f8;
      color: ${theme.palette.secondary.dark};
    `,
    linkItem: css`
      text-decoration: none;
      text-transform: uppercase;
      color: ${theme.palette.secondary.main};
      transition: 0.2s ease-in-out;

      &:hover {
        color: ${theme.palette.accent.main} !important;
        background-color: transparent !important;
      }
    `,
    activeLink: css`
      color: ${theme.palette.accent.main} !important;
    `,
    subLinkItem: css`
      text-decoration: none;
    `,
    logoContainer: {
      ...theme.customValues.flexCenter,
      height: '4rem',
      mb: {
        xs: '2rem',
        sm: '4rem',
      },
    },
    mainLinkContainer: {
      ...theme.customValues.flexCenter,
      flexDirection: { xs: 'column', sm: 'row' },
      mb: {
        xs: '2rem',
        sm: '1.5rem',
      },
      flexWrap: 'wrap',
      gap: {
        xs: '.75rem',
        sm: '2rem',
      },
    },
    subLinkContainer: {
      ...theme.customValues.flexCenter,
      flexDirection: { xs: 'column', sm: 'row' },
      mb: {
        xs: '2rem',
        sm: '4rem',
      },
      flexWrap: 'wrap',
      gap: {
        xs: '.75rem',
        sm: '2rem',
      },
    },
    registerCarDevButton: {
      marginBottom: '2rem',
      background: 'hotpink',
      color: theme.palette.getContrastText('#FF69B4'),// rgb of hotpink
    },
  };

  const devTools = () => (
    <>
      {registrationNumber && <p>Created car with reg number: {registrationNumber}</p>}
      {shouldShowDevtools() && userState?.user?.station && (
        <Button
        name={"register-car"}
        sx={styles.registerCarDevButton}
        onClick={
        () => {
          registerNewCar({ stationTracCode: userState.user.station })
            .then((response) => {
              setRegistrationNumber(response.data)
            })
        }}
        >
        REGISTER CAR!
      </Button>
      )}
    </>
  )

  return (
    <footer className={styles.container}>
      {userState?.user?.signedIn && (
        <>
          <Box sx={styles.logoContainer}>
            <HertzLogoType />
          </Box>

          {devTools()}
          <Box sx={styles.mainLinkContainer}>
            {MAIN_LINKS.map((item) =>
              !item.roleRequired ||
              userState?.user?.roles?.includes(item?.roleRequired) ? (
                <Link
                  key={item.translationKey}
                  to={item.location}
                  className={`${styles.linkItem} ${
                    isActive(item.location) && styles.activeLink
                  }`}
                >
                  <span>{t(item.translationKey)}</span>
                </Link>
              ) : null
            )}
          </Box>
          <Box
            sx={[
              theme.customValues.flexCenter,
              {
                flexDirection: { xs: 'column', sm: 'row' },
                mb: {
                  xs: '2rem',
                  sm: '1.5rem',
                },
                flexWrap: 'wrap',
                gap: {
                  xs: '.4rem',
                  sm: '0',
                },
              },
            ]}
          />
          <Box sx={styles.subLinkContainer}>
            {SUB_LINKS.map((item) => (
              <Link
                key={item.translationKey}
                to={item.location}
                className={`${styles.linkItem} ${styles.subLinkItem} ${
                  isActive(item.location) && styles.activeLink
                }`}
              >
                <span>{t(item.translationKey)}</span>
              </Link>
            ))}
          </Box>
        </>
      )}
      <Typography variant="subtitle2" color={theme.palette.secondary.main}>
        © The Hertz Corporation. All rights reserved.
      </Typography>
    </footer>
  );
};

export default React.memo(Footer);
